import React, { createContext, useContext, useState, useEffect } from 'react';
import { ThemeProvider as MuiThemeProvider } from '@mui/material/styles';
import { lightTheme, darkTheme } from '../styles/theme';

// Theme options
export const THEME_OPTIONS = {
  LIGHT: 'light',
  DARK: 'dark',
  SYSTEM: 'system'
};

// Create context
const ThemeContext = createContext(null);

// Custom hook to use the theme context
export const useTheme = () => useContext(ThemeContext);

export const ThemeProvider = ({ children }) => {
  // Get theme preference from localStorage or default to 'light'
  const [themePreference, setThemePreference] = useState(() => {
    const savedPreference = localStorage.getItem('themePreference');
    return savedPreference || THEME_OPTIONS.LIGHT;
  });

  // State to track the active theme
  const [activeTheme, setActiveTheme] = useState(themePreference === THEME_OPTIONS.DARK ? darkTheme : lightTheme);

  // Function to determine if system preference is dark mode
  const isSystemDarkMode = () => {
    return window.matchMedia && window.matchMedia('(prefers-color-scheme: dark)').matches;
  };

  // Update the active theme based on preference
  useEffect(() => {
    const updateActiveTheme = () => {
      if (themePreference === THEME_OPTIONS.SYSTEM) {
        setActiveTheme(isSystemDarkMode() ? darkTheme : lightTheme);
      } else {
        setActiveTheme(themePreference === THEME_OPTIONS.DARK ? darkTheme : lightTheme);
      }
    };

    updateActiveTheme();

    // Add listener for system preference changes
    const mediaQuery = window.matchMedia('(prefers-color-scheme: dark)');
    const handleChange = () => {
      if (themePreference === THEME_OPTIONS.SYSTEM) {
        setActiveTheme(isSystemDarkMode() ? darkTheme : lightTheme);
      }
    };

    // Add listener if browser supports it
    if (mediaQuery.addEventListener) {
      mediaQuery.addEventListener('change', handleChange);
    } else {
      // Fallback for older browsers
      mediaQuery.addListener(handleChange);
    }

    // Cleanup
    return () => {
      if (mediaQuery.removeEventListener) {
        mediaQuery.removeEventListener('change', handleChange);
      } else {
        mediaQuery.removeListener(handleChange);
      }
    };
  }, [themePreference]);

  // Function to change theme preference
  const changeTheme = (newTheme) => {
    setThemePreference(newTheme);
    localStorage.setItem('themePreference', newTheme);
  };

  return (
    <ThemeContext.Provider
      value={{
        themePreference,
        changeTheme,
        isDarkMode: activeTheme === darkTheme,
        themeOptions: THEME_OPTIONS
      }}
    >
      <MuiThemeProvider theme={activeTheme}>{children}</MuiThemeProvider>
    </ThemeContext.Provider>
  );
};
